import React, { useEffect, useRef, useState } from 'react';
import { Clock3, Eye, MapPin, MessageCircle, MoreVertical, Pencil, Power, Trash2 } from 'lucide-react';
import { useTranslations } from '../translations';
import './MyAnnouncementsCard.css';

type AnnouncementStatus = 'ACTIVE' | 'EXPIRED' | 'PENDING' | 'DECLINED';

interface MyAnnouncementsCardProps {
  id: string;
  title: string;
  imageUrl: string;
  location: string;
  datePosted: string;
  views: number;
  price: string;
  negotiable: boolean;
  messagesCount: number;
  status?: AnnouncementStatus;
  onClick?: (id: string) => void;
  onMessages?: (id: string, title: string) => void;
  onEdit?: (id: string) => void;
  onToggleStatus?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const MyAnnouncementsCard: React.FC<MyAnnouncementsCardProps> = ({
  id,
  title,
  imageUrl,
  location,
  datePosted,
  views,
  price,
  negotiable,
  messagesCount,
  status = 'ACTIVE',
  onClick,
  onMessages,
  onEdit,
  onToggleStatus,
  onDelete,
}) => {
  const t = useTranslations();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMenu(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMenu]);

  const statusLabels: Record<AnnouncementStatus, string> = {
    ACTIVE: t.myAnnouncements.statusActive,
    EXPIRED: t.myAnnouncements.statusExpired,
    PENDING: t.myAnnouncements.statusPending,
    DECLINED: t.myAnnouncements.statusDeclined,
  };
  const messagesLabel = messagesCount === 1
    ? t.myAnnouncements.oneMessage
    : t.myAnnouncements.messagesCount.replace('{count}', messagesCount.toString());
  const viewsLabel = views === 1
    ? t.myAnnouncements.oneView
    : t.myAnnouncements.viewsCount.replace('{count}', views.toString());
  const openLabel = t.myAnnouncements.openAnnouncement.replace('{title}', title);

  const handleOpen = () => onClick?.(id);
  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, select, textarea, [role="button"], [role="menuitem"]')) {
      return;
    }

    handleOpen();
  };
  const handleEdit = () => onEdit?.(id);
  const handleToggleStatus = () => onToggleStatus?.(id);
  const handleMessages = () => {
    if (messagesCount > 0) {
      onMessages?.(id, title);
    }
  };
  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowMenu(false);
    onDelete?.(id);
  };

  const handleMenuToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowMenu((isOpen) => !isOpen);
  };

  return (
    <article
      className="owner-announcement-card"
      data-status={status.toLowerCase()}
      onClick={handleCardClick}
    >
      <button
        type="button"
        className="owner-announcement-image"
        onClick={handleOpen}
        aria-label={openLabel}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" />
        ) : (
          <span className="owner-announcement-image-placeholder">{t.adminCard.noImage}</span>
        )}
      </button>

      <div className="owner-announcement-details">
        <div className="owner-announcement-summary">
          <div className="owner-announcement-title-row">
            <button
              type="button"
              className="owner-announcement-title"
              onClick={handleOpen}
              title={title}
            >
              {title}
            </button>
            <span className={`owner-announcement-status owner-announcement-status-${status.toLowerCase()}`}>
              {statusLabels[status]}
            </span>
          </div>

          <div className="owner-announcement-meta">
            <span className="owner-announcement-meta-item" title={location}>
              <MapPin size={15} aria-hidden="true" />
              <span>{location}</span>
            </span>
            <span className="owner-announcement-meta-item owner-announcement-date">
              <Clock3 size={15} aria-hidden="true" />
              <span>{datePosted}</span>
            </span>
          </div>

          <div className="owner-announcement-price-row">
            <strong className="owner-announcement-price">{price}</strong>
            {negotiable && (
              <span className="owner-announcement-negotiable">{t.productDetail.negotiable}</span>
            )}
          </div>
        </div>

        <div className="owner-announcement-performance" aria-label={t.myAnnouncements.performance}>
          <span className="owner-announcement-stat">
            <Eye size={17} aria-hidden="true" />
            <span>{viewsLabel}</span>
          </span>
          {messagesCount > 0 ? (
            <button
              type="button"
              className="owner-announcement-stat owner-announcement-messages-link"
              onClick={handleMessages}
              aria-label={t.myAnnouncements.openMessages.replace('{count}', messagesCount.toString())}
            >
              <MessageCircle size={17} aria-hidden="true" />
              <span>{messagesLabel}</span>
            </button>
          ) : (
            <span className="owner-announcement-stat">
              <MessageCircle size={17} aria-hidden="true" />
              <span>{messagesLabel}</span>
            </span>
          )}
        </div>

        <div className="owner-announcement-actions">
          {onEdit && (
            <button type="button" className="owner-announcement-action" onClick={handleEdit}>
              <Pencil size={16} aria-hidden="true" />
              <span>{t.common.edit}</span>
            </button>
          )}
          {onToggleStatus && (
            <button
              type="button"
              className={`owner-announcement-action owner-announcement-status-action ${status === 'ACTIVE' ? 'deactivate' : 'activate'}`}
              onClick={handleToggleStatus}
            >
              <Power size={16} aria-hidden="true" />
              <span>{status === 'ACTIVE' ? t.adminCard.deactivate : t.adminCard.activate}</span>
            </button>
          )}
        </div>

        {onDelete && (
          <div className="owner-announcement-menu" ref={menuRef}>
            <button
              type="button"
              className="owner-announcement-menu-button"
              onClick={handleMenuToggle}
              aria-label={t.myAnnouncements.moreActions}
              aria-haspopup="menu"
              aria-expanded={showMenu}
            >
              <MoreVertical size={20} aria-hidden="true" />
            </button>
            {showMenu && (
              <div className="owner-announcement-dropdown" role="menu">
                <button
                  type="button"
                  className="owner-announcement-dropdown-item"
                  onClick={handleDelete}
                  role="menuitem"
                >
                  <Trash2 size={16} aria-hidden="true" />
                  <span>{t.common.delete}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default MyAnnouncementsCard;
